import { ONFIDO_DOC_TYPE_PASSPORT } from 'apps/auth/constants/Onfido';

type DocumentTypes = Record<string, boolean>;
type DocumentStep = { type: 'document'; options?: { documentTypes?: DocumentTypes; hideCountrySelection?: boolean } };
type FaceStep = { type: 'face'; options?: { requestedVariant?: 'motion' } };
type OnfidoStep = DocumentStep | FaceStep | 'face' | 'complete' | 'welcome' | 'document';

type DocInfo = { type?: string };
type DocsParam = {
    motionRequired?: boolean;
    requiredSelfie?: unknown[];
    identificationRequired?: boolean;
};

type OnfidoInitOptions = {
    token: string;
    containerId: string;
    customUI?: Record<string, string> | null;
    onComplete?: () => void;
    steps: OnfidoStep[];
    onError?: (error: unknown) => void;
};

type OnfidoSdk = {
    init: (options: OnfidoInitOptions) => unknown;
};

const getSteps = (documentTypes: DocumentTypes = {}, addSelfie = true): OnfidoStep[] => {
    const stepsArray: OnfidoStep[] = [];
    stepsArray.push({
        type: 'document',
        options: {
            documentTypes: documentTypes,
            hideCountrySelection: true,
        },
    });
    if (addSelfie) {
        stepsArray.push('face');
    }
    stepsArray.push('complete');
    return stepsArray;
};

const getGT_Steps = (docInfo?: DocInfo, addSelfie = true, addMotion = false): OnfidoStep[] => {
    const stepsArray: OnfidoStep[] = [];

    if (docInfo) {
        stepsArray.push({
            type: 'document',
            options: {
                documentTypes: { [docInfo?.type ?? '']: true },
                hideCountrySelection: true,
            },
        });
    } else {
        stepsArray.push({
            type: 'document',
        });
    }

    if (addMotion) {
        stepsArray.push({
            type: 'face',
            options: {
                requestedVariant: 'motion',
            },
        });
    } else if (addSelfie) {
        stepsArray.push('face');
    }

    stepsArray.push('complete');
    return stepsArray;
};

const getMotionStep = (): OnfidoStep[] => {
    const stepsArray: OnfidoStep[] = [];

    stepsArray.push({
        type: 'face',
        options: {
            requestedVariant: 'motion',
        },
    });

    stepsArray.push('complete');
    return stepsArray;
};

const getThemeConfig = (theme?: string): Record<string, string> | null => {
    if (theme === 'dark') {
        return {
            colorBackgroundSurfaceModal: '#20252CFF',
            colorContentBody: '#FFFFFFFF',
            colorContentTitle: '#FFFFFFFF',
            colorContentSubtitle: '#FFFFFFFF',
            colorContentButtonPrimaryText: '#FFFFFFFF',
            colorContentButtonSecondaryText: '#FFFFFFFF',
            colorContentDocTypeButton: '#FFFFFFFF',
            colorBackgroundInput: '#3C3C3CFF',
            colorContentInput: '#FFFFFFFF',
        };
    }
    return null;
};

export class OnfidoService {
    static init = (
        Onfido: OnfidoSdk,
        accessToken: string,
        options: OnfidoStep[],
        onComplete?: () => void,
        onError?: (error: unknown) => void,
        theme?: string,
    ): unknown => {
        return Onfido.init({
            token: accessToken,
            containerId: 'onfido-mount',
            customUI: getThemeConfig(theme),
            onComplete: onComplete,
            steps: options,
            onError: onError,
        });
    };

    static getGtOptions = (): { steps: OnfidoStep[] } => {
        return {
            steps: ['welcome', 'document', 'complete'],
        };
    };

    static getCanadianOptions = (docInfo?: DocInfo, requiredSelfie: unknown[] = []): OnfidoStep[] => {
        const docType = docInfo?.type ?? '';
        const addSelfie = requiredSelfie?.length > 0;
        const documentTypes = { [docType]: true };
        return getSteps(documentTypes, addSelfie);
    };

    static getGT_Options = (docInfo?: DocInfo, requiredSelfie: unknown[] = []): OnfidoStep[] => {
        const addSelfie = requiredSelfie?.length > 0;
        return getGT_Steps(docInfo, addSelfie, false);
    };

    static getOptionsForPassport = (): OnfidoStep[] => {
        return getSteps({ [ONFIDO_DOC_TYPE_PASSPORT]: true }, true);
    };

    static getFraudOptions = (docInfo?: DocInfo, docsParam?: DocsParam): OnfidoStep[] => {
        const motionRequired = docsParam?.motionRequired;
        const addSelfie = (docsParam?.requiredSelfie?.length ?? 0) > 0;

        if (docsParam?.identificationRequired) {
            return getGT_Steps(docInfo, addSelfie, motionRequired);
        }

        return getMotionStep();
    };

    static getRecipientFraudOptions = (docInfo?: DocInfo, docsParam?: DocsParam): OnfidoStep[] => {
        const motionRequired = docsParam?.motionRequired;

        return getGT_Steps(docInfo, false, motionRequired);
    };

    static getMotionFraudOption = (): OnfidoStep[] => {
        const stepsArray: OnfidoStep[] = [];

        stepsArray.push({
            type: 'face',
            options: {
                requestedVariant: 'motion',
            },
        });
        return stepsArray;
    };
}
